import React, { useEffect, useMemo, useState } from 'react'
import { Calendar, Views, momentLocalizer } from 'react-big-calendar'

import { Box, Typography, useMediaQuery } from '@mui/material'
import { ReceivablesCalendar } from 'models/Receivables'
import moment from 'moment'
import 'moment/locale/pt-br'
import { ReceivablesService } from 'services/ReceivablesService'

import { ModalBalanceDetail } from 'components/Modal/ModalBalaceDetail/ModalBalaceDetail'

import { formatMoney } from 'utils'

import { EventsCalendar } from './CalendarReceivables.interface'
import * as S from './CalendarReceivables.styles'
import {
  formatMoneyCompact,
  Language,
  mountDateCalendar
} from './CalendarReceivables.utils'
import ListReceivables from './components/ListReceivables/ListReceivables'
import ToolbarCalendar from './components/ToolbarCalendar/ToolbarCalendar'
import SkeletonCalendar from './Skeleton'

// Mesmo padrao dos outros modulos que usam moment com data em portugues
// (ex.: ExpensesCalendar, *.config.ts): o locale do moment e global, entao
// cada modulo que depende dele fixa aqui pra nao ficar na ordem de import.
moment.locale('pt-br')

type ViewMode = 'calendar' | 'list'

interface CalendarReceivablesProps {
  balanceScheduled: number | false
  onExportReport: () => void
  exportLoading: boolean
}

const CalendarReceivables: React.FC<CalendarReceivablesProps> = ({
  balanceScheduled,
  onExportReport,
  exportLoading
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [records, setRecords] = useState<Array<ReceivablesCalendar>>([])
  const [viewMode, setViewMode] = useState<ViewMode>('calendar')
  const [modalBalaceDetail, setModalBalaceDetail] = useState({
    isOpen: false,
    date: ''
  })

  const localizer = momentLocalizer(moment)
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })

  function handleCloseModalBalaceDetail() {
    setModalBalaceDetail({
      date: '',
      isOpen: false
    })
  }

  // A celula do dia tem ~51px no mobile, onde "R$ 1.230,90" a 16px nao cabe
  // (virava "R$ ..." ou era clipada de vez quando a linha do mes encurtava).
  // Ali o valor vai compacto ("1,2k"); o exato sai no modal do dia.
  const events: Array<EventsCalendar> = useMemo(
    () =>
      records.map(({ amount, date }, index) => {
        const dateSplited = date.split('-')
        return {
          id: index + 1,
          start: mountDateCalendar(dateSplited, true),
          end: mountDateCalendar(dateSplited, false),
          title: isMobile ? (
            <Typography color="black" fontSize={10} fontWeight={600}>
              {formatMoneyCompact(amount)}
            </Typography>
          ) : (
            <Typography color="black">{formatMoney(amount)}</Typography>
          )
        }
      }),
    [records, isMobile]
  )

  function handleOpenDetailOfTheDay(event: EventsCalendar) {
    setModalBalaceDetail({
      isOpen: true,
      date: moment(event.start).format('YYYY-MM-DD')
    })
  }

  async function getInitial() {
    try {
      setLoading(true)

      const response = await ReceivablesService.getCalendarDetails()

      setRecords(response)
    } catch (err) {
      //
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getInitial()
  }, [])

  if (viewMode === 'list') {
    return (
      <ListReceivables
        onExportReport={onExportReport}
        exportLoading={exportLoading}
        onBackToCalendar={() => setViewMode('calendar')}
      />
    )
  }

  if (loading) {
    return (
      <Box sx={{ overflow: 'hidden' }} height="calc(60vh + 35px)">
        <SkeletonCalendar />
      </Box>
    )
  }

  return (
    <S.Wrapper>
      <Calendar
        localizer={localizer}
        events={events}
        messages={Language}
        onSelectEvent={handleOpenDetailOfTheDay}
        views={[Views.MONTH]}
        defaultView={Views.MONTH}
        components={{
          toolbar: (props) => (
            <ToolbarCalendar
              balanceScheduled={balanceScheduled}
              viewMode={viewMode}
              onChangeViewMode={setViewMode}
              {...props}
            />
          )
        }}
      />

      <ModalBalanceDetail
        handleClose={handleCloseModalBalaceDetail}
        date={modalBalaceDetail.date}
        isOpen={modalBalaceDetail.isOpen}
      />
    </S.Wrapper>
  )
}

export default CalendarReceivables
